import { createRef, FC, MouseEvent, useContext, useState } from "react";
import {
  Box,
  CircularProgress,
  createStyles,
  Link,
  makeStyles,
} from "@material-ui/core";
import axios from "axios";
import { saveAs } from "file-saver";
import ReCAPTCHA from "react-google-recaptcha";

import { CookieConsentRequired, RecaptchaTermsNotice } from "components";
import { AppContext } from "contexts";
import useTermlyConsent from "hooks/useTermlyConsent";
import { getFileNameFromContentDisposition } from "services/documents";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    recaptcha: {
      "& .grecaptcha-badge": {
        visibility: "hidden",
      },
    },
    link: {
      color: theme.custom.palette.blue.main,
      fontSize: "1rem",
      "&:hover": {
        color: theme.custom.palette.blue.main,
      },
      lineHeight: "1.5rem", // prevents jump when loader renders
    },
    loader: {
      marginLeft: theme.spacing(2),
    },
    loaderOffset: {
      marginLeft: "32px", // maintains centering when loader renders
    },
    nowrap: {
      whiteSpace: "nowrap",
    },
  }),
);

interface RawDataDownloadProps {
  earningsFile: string;
}

const RawDataDownload: FC<RawDataDownloadProps> = ({ earningsFile }) => {
  const classes = useStyles();

  const { user } = useContext(AppContext);

  const [isRawDataLoading, setIsRawDataLoading] = useState(false);
  const [userHasTriggeredCaptcha, setUserHasTriggeredCaptcha] = useState(false);

  const isRecaptchaAllowed = useTermlyConsent(["performance"]);

  const recaptchaRef = createRef<ReCAPTCHA>();

  const handleTriggerCaptcha = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setUserHasTriggeredCaptcha(true);
    if (!!recaptchaRef.current) {
      recaptchaRef.current.reset(); // forces execute even if user has recently passed captcha
      recaptchaRef.current.execute();
    }
  };

  const handleDownloadDataFile = async (url: string) => {
    try {
      setIsRawDataLoading(true);
      const response = await axios.get(url);
      const fileName = getFileNameFromContentDisposition(
        response.headers["content-disposition"],
      );

      saveAs(new Blob([response.data]), fileName);
    } catch {
      // pass
    } finally {
      setIsRawDataLoading(false);
    }
  };

  return (
    <Box>
      {!!user && !!earningsFile && (
        <>
          {(user.isAAI || isRecaptchaAllowed) && (
            <Link
              className={`${classes.link} ${classes.loaderOffset}`}
              href={earningsFile}
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                if (user.isAAI) {
                  e.preventDefault();
                  handleDownloadDataFile(earningsFile);
                } else {
                  handleTriggerCaptcha(e);
                }
              }}
            >
              Download Raw Data File (.csv)
              {isRawDataLoading ? (
                <CircularProgress size={16} className={classes.loader} />
              ) : (
                <Box width={32} display="inline-block" />
              )}
            </Link>
          )}

          {!user.isAAI && (
            <>
              {isRecaptchaAllowed ? (
                <>
                  <Box className={classes.recaptcha}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      size="invisible"
                      badge="bottomleft"
                      sitekey={
                        import.meta.env.REACT_APP_RECAPTCHA_SITE_KEY || ""
                      }
                      onChange={(token: string) =>
                        handleDownloadDataFile(
                          `${earningsFile}?captcha=${token}`,
                        )
                      }
                      onExpired={() => null}
                    />
                  </Box>
                  {userHasTriggeredCaptcha && <RecaptchaTermsNotice />}
                </>
              ) : (
                <Box mt={1}>
                  <CookieConsentRequired
                    message={
                      <>
                        <strong>Performance</strong> cookies are required to{" "}
                        <strong className={classes.nowrap}>
                          Download Raw Data File
                        </strong>
                        .
                      </>
                    }
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default RawDataDownload;
